import EmptySearchResults from 'src/modules/search/shared/components/emptySearchResults';
import PostList from 'src/modules/shared/resultsList/components/postList';
import PostListLoader from 'src/modules/shared/resultsList/components/postListLoader';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event } from 'src/constants/footfall';
import HelpInfoBox from 'src/modules/search/shared/components/helpInfoBox';
import { LinkButton } from 'styles-js/buttons';
import i18n from 'src/utils/translate';
import { useDispatch, useSelector } from 'src/store/store';
import { showModal } from 'src/modules/shared/modal/actions';
import { ModalType } from 'src/constants/modalTypes';
import Pagination from 'src/components/pagination/pagination';
import paginationResultsPerPage from 'src/constants/paginationResultsPerPage';
import { redirectToSearchPage } from 'src/utils/router';

type Props = {
  isCommunitySearch: boolean;
  isMemberOfCommunity: boolean;
}
export default function SearchResults({
  isCommunitySearch,
  isMemberOfCommunity,
}: Props) {
  const dispatch = useDispatch();
  const communitySlug = useSelector(({ context }) => context.currentCommunity?.slug);
  const communityName = useSelector(({ context }) => context.currentCommunity?.name);

  const {
    communityFilter = 'all',
    isLoading,
    searchId,
    searchQuery = '',
    searchResults,
    totalResults = 0,
  } = useSelector(({ pages }) => pages.search);

  const showFilterModal = (e) => {
    e.preventDefault();
    dispatch(showModal({ modalType: ModalType.SearchFilterOptions }));
  };

  const showSearchSortBy = () => {
    dispatch(showModal({ modalType: ModalType.SearchSortBy }));
  };

  let helpInfoText = i18n.t("You're searching hundreds of public communities on HealthUnlocked.");
  let helpInfoButtonText = i18n.t('Filter results');
  let helpInfoOnClick = (e) => {
    showFilterModal(e);
    window.scrollTo(0, 0);
  };

  if (isCommunitySearch) {
    helpInfoText = i18n.t(`You're searching only inside ${communityName}.`);
    helpInfoButtonText = i18n.t('Search all of HealthUnlocked');
    helpInfoOnClick = () => {
      window.scrollTo(0, 0);
      redirectToSearchPage({ query: searchQuery });
    };
  }

  const count = Number(totalResults).toLocaleString();
  const typeOfPost = !isMemberOfCommunity ? i18n.t('public') + ' ' : '';
  const postLabel = totalResults === 1 ? i18n.t('post') : i18n.t('posts');
  const resultsCount = `${count} ${typeOfPost}${postLabel}`;

  if (isLoading) {
    return <PostListLoader />;
  }

  if (searchResults.length === 0) {
    return (
      <EmptySearchResults
        communitySlug={communitySlug}
        isFiltered={communityFilter !== 'all'}
        isMemberOfCommunity={isMemberOfCommunity}
        query={searchQuery}
      />
    );
  }

  return (
    <>
      <div>
        <div className="search__summary-row" data-testid="search-summary-row">
          <div>{resultsCount}</div>
          {isCommunitySearch ? (
            <LinkButton className="mobile-filter-modal" onClick={showSearchSortBy}>
              {i18n.t('Sort results')}
            </LinkButton>
          ) : (
            <LinkButton className="mobile-filter-modal" onClick={showFilterModal}>
              {i18n.t('Filter results')}
            </LinkButton>
          )}
        </div>
        <PostList
          isCommunitySearch={!!isCommunitySearch}
          posts={searchResults}
          onPostItemClick={(linkType, postId) => {
            trackFootfallEvent(Event.CLICKED_SEARCH_RESULT, {
              clickType: 'post-result',
              clickMetadata: { postId, linkType, searchId },
            });
          }}
        />
      </div>
      <div data-testid="search-pagination">
        <Pagination
          pageSize={paginationResultsPerPage.search}
          total={totalResults}
        />
      </div>
      <HelpInfoBox
        buttonText={helpInfoButtonText}
        text={helpInfoText}
        onClick={helpInfoOnClick}
      />
    </>
  );
}
